import React, { useEffect, useState } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import { CheckCircle } from "lucide-react";
import { Link } from "react-router-dom";

const ProgramPage: React.FC = () => {
  const [selectedPlan, setSelectedPlan] = useState<number | null>(null);
  useEffect(() => {
    AOS.init({ duration: 500, once: true });
  }, []);

  const programData = [
    {
      title: "Personalized Speaking Practice",
      desc: "Develop your speaking skills through focused practice tailored to your proficiency level, learning goals, and individual needs",
    },
    {
      title: "Real-Life Indonesian Conversation",
      desc: "Build the confidence to communicate naturally in everyday situations using Indonesian as it is genuinely spoken",
    },
    {
      title: "Pronunciation & Accent",
      desc: "Refine your pronunciation and intonation to speak more clearly, naturally, and confidently",
    },
    {
      title: "Grammar for Communication",
      desc: "Strengthen your understanding of practical grammar and learn how to apply it effectively in real conversations",
    },
    {
      title: "Listening & Comprehension",
      desc: "Improve your ability to follow naturally spoken Indonesian across different contexts, speeds, and conversational styles",
    },
    {
      title: "Cultural Understanding",
      desc: "Gain deeper insight into Indonesian culture and the cultural nuances that shape how the language is used",
    },
  ];

  const listBenefits = [
    {
      title: "Personalized Curriculum",
    },
    {
      title: "Free Placement Test",
    },
    {
      title: "Student Progress Report",
    },
    {
      title: "Intensive Tutor Support",
    },
    {
      title: "E-Certificate & Recording",
    },
  ];

  const planLists = [
    {
      title: "4 Sessions",
      desc: "IDR 237,000 / Session",
      priceLabel: "948,000",
    },
    {
      title: "12 Sessions",
      desc: "IDR 220,000 / Session",
      priceLabel: "2,639,000",
    },
    {
      title: "8 Sessions",
      desc: "IDR 230,000 / Session",
      priceLabel: "1,839,000",
    },
  ];

  const whiteIndexes = [1, 2, 5];
  const whiteAreas = [0, 2];

  const selectedPlanText =
    selectedPlan !== null
      ? `${planLists[selectedPlan].title} - IDR ${planLists[selectedPlan].priceLabel}`
      : "Private Class";

  const whatsappMessage = encodeURIComponent(
    selectedPlan !== null
      ? `Halo Fluenesia, saya ingin memesan paket ${planLists[selectedPlan].title} (${planLists[selectedPlan].priceLabel}) untuk Private Class. Mohon informasi lebih lanjut.`
      : `Halo Fluenesia, saya ingin memesan Private Class. Mohon informasi paket dan harga lebih lanjut.`,
  );

  const whatsappUrl = `https://api.whatsapp.com/send/?phone=6282324433012&text=${whatsappMessage}&type=phone_number&app_absent=0`;

  return (
    <div className="relative w-full min-h-screen bg-[#FAFAFA] font-sans overflow-x-hidden">
      <div className="absolute top-0 left-0 w-full z-0">
        <img
          src="/Mentor/screen 3.png"
          alt="Decoration"
          className="top-0 h-full object-contain "
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-8 md:pt-14 pb-24">
        {/* GRID SYSTEM */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* --- KOLOM KIRI (HEADER + BODY TEXT) --- */}
          <div className="lg:col-span-8 space-y-12">
            {/* A. Bagian Header (Hero) */}
            <div data-aos="fade-right">
              {/* --- UPDATE: Breadcrumb dengan Link --- */}
              <div className="flex items-center space-x-2 font-medium text-sm md:text-base mb-3">
                <Link
                  to="/"
                  className="hover:underline cursor-pointer transition-colors"
                >
                  Home
                </Link>
                <span>▸</span>
                <Link
                  to="/program-selection"
                  className="hover:underline cursor-pointer transition-colors"
                >
                  Program
                </Link>
                <span>▸</span>
                <span className="text-[#FF0000]">Private</span>
              </div>
              {/* ----------------------------------- */}

              <h1 className="text-4xl md:text-6xl font-bold text-black leading-tight tracking-tight mb-3">
                Private Class (1-on-1)
              </h1>

              <p className="text-lg md:text-xl text-gray-800 font-medium mb-6">
                Learn Indonesian through a personalized, one-on-one learning
                experience designed around your goals, interests, and pace of
                learning
              </p>

              {/* <button className="bg-[#FF0000] hover:bg-[#D6001C] text-white font-bold py-3 px-8 rounded-lg shadow-md transition-transform duration-300 transform hover:scale-105">
                Start Learning!
              </button> */}
            </div>

            {/* B. Bagian Body (Content Loop) */}
            {/* Padding top (pt) ditambahkan agar ada jarak pasca background pink habis */}
            <div className="space-y-6 pt-16 sm:pt-24 md:pt-24">
              <div data-aos="fade-up">
                <h3 className="text-2xl md:text-3xl font-bold text-black mb-4">
                  What You'll Work On
                </h3>
              </div>

              {/* Program Features */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-4 -pt-10">
                {programData.map((item, index) => (
                  <div
                    key={index}
                    className={`${whiteIndexes.includes(index) ? "bg-white border-[#FFB0B3] border-2" : "bg-[#FFE6E6]"} rounded-[30px] p-8 md:p-10 hover:shadow-lg transition-shadow duration-300 flex flex-col items-start text-left`}
                    data-aos="fade-up"
                    data-aos-delay={index * 100}
                  >
                    <div className="shrink-0 mt-0.5 mb-6">
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect width="20" height="20" rx="4" fill="#00C800" />
                        <path
                          d="M5 10L8.5 13.5L15 6"
                          stroke="white"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>

                    {/* Content */}
                    <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed text-sm md:text-base">
                      {item.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>
            {/* Mentor Spotlight */}
            <div className="flex flex-col gap-4">
              <div className="bg-[#FFEBEB] border border-[#CC0007] rounded-[30px] p-8 md:p-10 hover:shadow-lg transition-shadow duration-300 flex text-left w-full items-center justify-between">
                <div className="w-24 h-24 border bg-[#ed8a8e] rounded-full flex-shrink-0">
                  <img
                    src="/Mentor/Ida.jpeg"
                    alt="Mentor Ida"
                    className="w-full h-full object-cover rounded-full"
                  />
                </div>

                <div className="flex flex-col justify-center pl-10 w-full">
                  <div className="bg-[#FFB0B3] px-5 w-max py-1 rounded-full mb-2">
                    <span className="text-[#CC0007] text-sm font-medium">
                      Mentor Spotlight
                    </span>
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold text-gray-900">
                    Ida Fitriyah
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-sm md:text-base text-wrap w-3/4">
                    Universitas Sebelas Maret - Pendidikan Bahasa Indonesia
                  </p>
                </div>
              </div>

              <div className="bg-[#FFEBEB] border border-[#CC0007] rounded-[30px] p-8 md:p-10 hover:shadow-lg transition-shadow duration-300 flex text-left w-full items-center justify-between">
                <div className="w-24 h-24 border bg-[#ed8a8e] rounded-full flex-shrink-0 overflow-hidden">
                  <img
                    src="/Mentor/Dira.jpeg"
                    alt="Mentor Dira"
                    className="w-full h-full object-cover object-[10%_1%] rounded-full"
                  />
                </div>

                <div className="flex flex-col justify-center pl-10 w-full">
                  <div className="bg-[#FFB0B3] px-5 w-max py-1 rounded-full mb-2">
                    <span className="text-[#CC0007] text-sm font-medium">
                      Mentor Spotlight
                    </span>
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold text-gray-900">
                    Dian Rachmatika
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-sm md:text-base text-wrap w-3/4">
                    University of Southampton, UK - English Language Teaching
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* --- KOLOM KANAN (CARD FLOATING) --- */}
          <div className="lg:col-span-4 lg:pt-10 relative" data-aos="fade-left">
            <img
              src="/Programs/private-class.jpeg"
              alt="Private Class"
              className="w-full h-full object-cover rounded-2xl"
            />
            <div className="bg-white rounded-3xl shadow-2xl p-6 w-full border border-gray-100 sticky top-8">
              <div className="bg-[#CC0007] py-10 mb-4 -mt-6 -mx-6 rounded-tl-3xl rounded-tr-3xl items-center flex flex-col">
                <h1 className="text-2xl md:text-3xl font-bold text-white">
                  Select your Plan
                </h1>
                <p className="text-white">
                  Flexible sessions for rapid progress
                </p>
              </div>

              <div className="space-y-3 mb-8">
                {/* Rating */}
                <div className="flex flex-col pt-2 justify-center items-center gap-3.5 w-full">
                  {planLists.map((stat, index) => (
                    <span
                      key={index}
                      className={`relative p-4 ${whiteAreas.includes(index) ? "bg-[#ffffff] border-2 border-[#FFB0B3]" : "bg-[#FFEBEB] border-2 border-[#CC0007]"} text-black text-sm font-medium rounded-2xl w-full flex items-center justify-center gap-2`}
                    >
                      {!whiteAreas.includes(index) && (
                        <span
                          className={`absolute -top-3 left-1/2 -translate-x-1/2 bg-[#CC0007] text-white text-xs py-0.5 px-3 rounded-full whitespace-nowrap`}
                        >
                          Best Value
                        </span>
                      )}
                      <div className="flex items-center space-x-2 w-full">
                        <label
                          className="w-full flex justify-between items-center space-x-2 cursor-pointer"
                          htmlFor={`plan-${index}`}
                        >
                          <div className="flex items-center space-x-3">
                            <input
                              type="radio"
                              className="peer w-4 h-4 accent-[#CC0007] border-[#CC0007]"
                              name="plan"
                              id={`plan-${index}`}
                              value={index}
                              checked={selectedPlan === index}
                              onChange={() => setSelectedPlan(index)}
                            />
                            <div className="flex flex-col">
                              <h1 className="font-bold">{stat.title}</h1>
                              <p className="text-sm text-gray-600">
                                {stat.desc}
                              </p>
                            </div>
                          </div>
                          <div className="flex flex-col items-end">
                            <span className="text-sm text-[#CC0007]">IDR</span>
                            <p className="text-sm font-bold text-[#CC0007]">
                              {stat.priceLabel}
                            </p>
                          </div>
                        </label>
                      </div>
                    </span>
                  ))}
                </div>

                <div
                  style={{
                    borderTop: "none",
                    backgroundImage:
                      "linear-gradient(to right, #d1d5db 50%, transparent 50%)",
                    backgroundSize: "25px 1px",
                    backgroundRepeat: "repeat-x",
                    height: "1px",
                  }}
                />
                {/* Learners */}
                <div className="flex items-start space-x-3">
                  <span className="text-black text-lg font-medium">
                    Program Benefit's
                  </span>
                </div>
                {/* Duration */}
                {listBenefits.map((benefit, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <span className="text-gray-700">
                      <CheckCircle className="w-4 h-4 accent-[#CC0007]" />
                    </span>
                    <span className="text-black text-sm font-medium">
                      {benefit.title}
                    </span>
                  </div>
                ))}
              </div>

              <a href={whatsappUrl}>
                <button className="w-full bg-[#FF0000] hover:bg-[#D6001C] text-white font-bold py-3 px-4 rounded-xl shadow-md transition-all duration-300 transform hover:scale-[1.02]">
                  Start Learning!
                </button>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgramPage;
